import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import { FieldItem, Values } from '@lib/interfaces/form';
import Form, {
  ActionsProps,
} from '@lib/components/ReactHookForm/FormContainer';
import Modal from '@lib/components/Modal/Modal';
import { FormRefType } from '@lib/components/ReactHookForm/types';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import FormActions from './LegendModalFormActions';
import getFormFields from '../utils/getLegendFormFields';
import getStatusOptions from '../utils/getStatusOptions';
import getStatusOption from '../utils/getStatusOption';
import { MonitoringEntity, MonitoringType, StatusKind } from '../enums';
import { PerformanceLegend } from '../types';

function buildLegendModalHeader({
  t,
  monitoringEntity,
  monitoringType,
  statusKind,
}: {
  t: (k: string) => string;
  monitoringEntity: MonitoringEntity;
  monitoringType: MonitoringType;
  statusKind?: StatusKind;
}) {
  const statusOption = getStatusOption({ t, status: statusKind })?.data || {};

  let modalTitle = '';

  const statusKindLabel = statusKind
    ? getStatusOptions({ t }).find(({ value }) => value === statusKind)?.label
    : undefined;

  const entityLabel =
    monitoringEntity === MonitoringEntity.Site ? t('sites') : t('sectors');

  if (monitoringType === MonitoringType.Anomaly) {
    modalTitle = t('customize-legend');
  } else if (monitoringType === MonitoringType.Performance) {
    modalTitle = t('customize-performance-legend');
  }

  const chips: React.ReactNode[] = [
    <Chip
      key="entity"
      type={ChipTypes.chipGray}
      size={ChipSizes.small}
      label={entityLabel}
    />,
  ];
  if (statusKindLabel) {
    chips.push(
      <Chip
        {...statusOption}
        key="status"
        type={ChipTypes.chipGray}
        size={ChipSizes.small}
        label={statusKindLabel}
      />,
    );
  }

  return { modalTitle, renderAfterCaption: chips };
}

export interface FormProps {
  isEditForm?: boolean;
  isOpen: boolean;
  onCancel: () => void;
  onClose: () => void;
  onSubmitCallback: (values: Values) => void;
  formRef: FormRefType;
  legendData?: PerformanceLegend | null;
  monitoringEntity: MonitoringEntity;
  monitoringType: MonitoringType;
  statusKind?: StatusKind;
}

export default function LegendModalForm(props: FormProps) {
  const {
    isOpen,
    onCancel,
    onClose,
    onSubmitCallback,
    formRef,
    legendData,
    monitoringEntity,
    monitoringType,
    statusKind,
  } = props;
  const { t } = useTranslation();

  const fields: FieldItem[] = getFormFields({
    t,
    monitoringType,
  });

  const defaultFormValues = useMemo(() => {
    const items = _get(legendData, 'items', []);
    if (!items) return { items: [] };
    return {
      items: items.map((item: any) => {
        return {
          color: item?.color
            ? { value: item.color, label: item.color }
            : undefined,
          from: item?.from?.toString(),
          to: item?.to?.toString(),
        };
      }),
    };
  }, [legendData]);

  const actionsComponent: (
    actionsComponentProps: ActionsProps,
  ) => React.ReactElement = (actionsComponentProps) => (
    <FormActions {...actionsComponentProps} onCancel={onCancel} />
  );

  const actionsComponentProps = { onCancel };

  const onSubmitHandler = (values: Values) => {
    const itemsValue = _get(values, 'items', []) as Values[];
    const items = itemsValue.map((item: any) => ({
      color: _get(item, ['color', 'value'], '') || null,
      from: Number(item?.from),
      to:
        item?.to === '' || item?.to === null || item?.to === undefined
          ? null
          : Number(item?.to),
    }));
    onSubmitCallback({ entity: monitoringEntity, kind: monitoringType, items });
  };

  const { modalTitle, renderAfterCaption } = buildLegendModalHeader({
    t,
    monitoringEntity,
    monitoringType,
    statusKind,
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      caption={`${modalTitle}:`}
      renderAfterCaption={renderAfterCaption}
    >
      <Form
        actions={actionsComponent}
        actionsComponentProps={actionsComponentProps}
        defaultValues={defaultFormValues}
        fields={fields}
        formId="checklist-select-modal-form"
        formRef={formRef}
        reValidateMode="onChange"
        onSubmit={onSubmitHandler}
        sourceAppendValues={{
          items: {
            to: null,
            from: null,
            color: { value: 'green' },
          },
        }}
      />
    </Modal>
  );
}
